import React from 'react';
import { Redirect } from 'react-router-dom';
import { Route } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { isUserAuthenticated, getLoggedInUserRole } from '../helpers/authUtils';
// MUI Icons
import HomeIcon from '@mui/icons-material/Home';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import LockIcon from '@mui/icons-material/Lock';
import CalculateIcon from '@mui/icons-material/Calculate';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import DescriptionIcon from '@mui/icons-material/Description';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ApartmentIcon from '@mui/icons-material/Apartment';

// auth
const Logout = React.lazy(() => import('../pages/userauth/Logout'));
const Login = React.lazy(() => import('../pages/userauth/Login'));
const ForgotPassword = React.lazy(() => import('../pages/userauth/forgotPassword'));
const AccountRecover = React.lazy(() => import('../pages/userauth/accountRecover'));
const Register = React.lazy(() => import('../pages/userauth/register'));
const Register2fa = React.lazy(() => import('../pages/userauth/register2fa'));

//  User Management
const AdminUsersDashboard = React.lazy(()=> import('../pages/userDashboard/usersDashboard'));

// pages
const HomeDashboard = React.lazy(() => import('../pages/dashboard')); //the home page
const LoanTables = React.lazy(() => import('../pages/LoanDashboard/LoansTable'));
const LoanTables60Days = React.lazy(() => import('../pages/LoanDashboard/LoansTable60Days'));
const CashflowTable = React.lazy(() => import('../pages/LoanDashboard/LoansCashFlowReport'));
const LoansMaturityReport = React.lazy(() => import('../pages/LoanDashboard/LoansMaturityReport'));
const LoanDetails = React.lazy(() => import('../pages/LoanDetails/LoanDetails'));
const SecuritiesTable = React.lazy(() => import('../pages/SecurityDashboard/SecuritiesTable'));
const SecurityDetails = React.lazy(() => import('../pages/SecurityDetails/SecurityDetails'));
const ToolsDashboard = React.lazy(() => import('../pages/tools'));
const ReportsDashboard = React.lazy(() => import('../pages/Reports/ReportsDashboard'));
const LenderDetails = React.lazy(() => import('../pages/LenderDetails/LenderDetails'));
const LendersTable = React.lazy(() => import('../pages/LenderDashboard/lendersTable'));
const PropertyDetails = React.lazy(() => import('../pages/PropertyDetails/PropertyDetails'));
const PropertiesTable = React.lazy(() => import('../pages/PropertiesDashboard/PropertiesTable'));

const ErrorPageNotFound = React.lazy(() => import('../pages/error/PageNotFound'));
const NotAuthorizedPage = React.lazy(() => import('../pages/error/NotAuthorized'));
const UnderConstruction = React.lazy(() => import('../pages/error/UnderConstruction'));


/**
        0   → Anyone here is used as "Route"

 route: privateRoute
 Roles: 
        1 → Regular User

        2 → Manager (can see reports, Maybe Cashflow)

        3 → Admin (User Management {Add, Delete, Reset Password email for user to do it} , Refresh loans API Source, Cashflow, All Reports}

 */

// handle auth and authorization
const PrivateRoute = ({ component: Component, roles, ...rest }) => {
  const reduxUser = useSelector(state => state.Auth.user);
  const authOK = isUserAuthenticated() && reduxUser;

  return (
    <Route
      {...rest}
      render={props => {
        if (!authOK) {
          return <Redirect to={{ pathname: '/account/login', state: { from: props.location } }} />;
        }
        const userRole = getLoggedInUserRole()?.toString();
        if (roles && roles.indexOf(userRole) === -1) {
          return <Redirect to="/error/NotAuthorized" />;
        }
        return <Component {...props} />;
      }}
    />
  );
};

// root routes
const rootRoute = {
  path: '/',
  exact: true,
  component: () => <Redirect to="/dashboard" />, // Makes dashboard the main page of the app
  route: PrivateRoute,
  navItem: false,
};

// pages
const homeDashbordRoutes = {
  path: '/dashboard',
  name: 'Home',
  icon: <HomeIcon />,
  header: 'Custom',
  component: (props) => <HomeDashboard {...props} />,
  //the props is any parameter ... it is used to call .getLoans() & .getSecurites() in home 
  route: PrivateRoute,
  roles: ['1', '2', '3'],
  exact: true,
  navItem: true,
};

const loanRoutes = {
  path: '/loans',
  name: 'Loans',
  icon: <AttachMoneyIcon />,
  header: 'Custom',
  navItem: true,
  children: [
    {
      path: '/loans/dashboard',
      name: 'Loans Dashboard',
      component: () => <LoanTables />,
      route: PrivateRoute,
      roles: ['1', '2', '3'],
      exact: true,
      navItem: true,
    },
    {
      path: '/loans/60-day',
      name: '60 Day Report',
      component: () => <LoanTables60Days />,
      route: PrivateRoute,
      roles: ['2', '3'],
      exact: true,
      navItem: true,
    },
    {
      path: '/loans/cash-flow',
      name: 'Cash Flow Report',
      component: CashflowTable,
      route: PrivateRoute,
      roles: ['3'],
      exact: true,
      navItem: true,
    },
    {
      path: '/loans/maturity',
      name: 'Maturity Report',
      component: LoansMaturityReport,
      route: PrivateRoute,
      roles: ['2', '3'],
      exact: true,
      navItem: true,
    },
    {
      path: '/loans/:loanId',
      name: 'Loan Details',
      component: LoanDetails,
      route: PrivateRoute,
      roles: ['1', '2', '3'],
      exact: true,
      navItem: false,
    },
  ],
};

const securityRoutes = {
  path: '/securities',
  name: 'Securities',
  icon: <LockIcon />,
  header: 'Custom',
  navItem: true,
  children: [
    {
      path: '/securities/dashboard',
      name: 'Security Dashboard',
      component: SecuritiesTable,
      route: PrivateRoute,
      roles: ['1', '2', '3'],
      exact: true, 
      navItem: true,
    },
    {
      path: '/securities/:securityId',
      name: 'Security Details',
      component: SecurityDetails,
      route: PrivateRoute,
      roles: ['1', '2', '3'],
      exact: true,
      navItem: false,
    },
  ],
};

const lenderRoutes = {
  path: '/lenders',
  name: 'Lenders',
  icon: <AccountBalanceIcon />,
  header: 'Custom',
  navItem: true,
  children: [
    {
      path: '/lenders/dashboard',
      name: 'Lenders Dashboard',
      component: LendersTable,
      route: PrivateRoute,
      roles: ['1', '2', '3'],
      exact: true,
      navItem: true,
    },
    {
      path: '/lenders/:lenderId',
      name: 'Lenders Details',
      component: LenderDetails,
      route: PrivateRoute,
      roles: ['1', '2', '3'],
      exact: true,
      navItem: false,
    },
  ],
};

const PropertiesRoutes = {
  path: '/properties',
  name: 'Properties',
  icon: <ApartmentIcon />,
  header: 'Custom',
  navItem: true,
  children: [
    {
      path: '/properties/dashboard',
      name: 'Properties Dashboard',
      component: PropertiesTable,
      route: PrivateRoute,
      roles: ['1', '2', '3'],
      exact: true,
      navItem: true,
    },
    {
      path: '/properties/:propertyId',
      name: 'Properties Details',
      component: PropertyDetails,
      route: PrivateRoute,
      roles: ['1', '2', '3'],
      exact: true,
      navItem: false,
    },
  ],
};

const toolsRoutes = {
  path: '/tools',
  name: 'Tools',
  icon: <CalculateIcon/>,
  header: 'Custom',
  navItem: true,
  children: [
    {
      path: '/tools/loan-sizer',
      name: 'Loan Sizer',
      //component: (props) => <ToolsDashboard {...props} tool="loan-sizer" />,
      component: UnderConstruction,
      route: PrivateRoute,
      roles: ['1', '2', '3'],
      exact: true,
      navItem: true,
    },
    {
      path: '/tools/ltv-calculator',
      name: 'LTV Calc',
      //component: (props) => <ToolsDashboard {...props} tool="ltv" />,
      component: UnderConstruction,
      route: PrivateRoute,
      roles: ['1', '2', '3'],
      exact: true,
      navItem: true,
    },
  ],
}

const userManagement = {
  path: '/admin/users-dashboard',
  name: 'User Management',
  icon: <GroupAddIcon />,
  header: 'Custom',
  component: AdminUsersDashboard,
  route: PrivateRoute,
  roles: ['3'],
  exact: true,
  navItem: true,
};

const reportsRoutes = {
  path: '/reports/dashboard',
  name: 'Reports',
  icon: <DescriptionIcon/>,
  header: 'Custom',
  component: ReportsDashboard,
  route: PrivateRoute,
  roles: ['1', '2', '3'],
  exact: true,
  navItem: true,
};


// auth
const authRoutes = {
  path: '/account',
  name: 'Auth',
  navItem: false,
  children: [
    {
      path: '/account/login',
      name: 'Login',
      component: Login,
      route: Route,
      navItem: false,
    },
    {
      path: '/account/forgot-password',
      name: 'Forgot Password',
      component: ForgotPassword,
      route: Route,
      navItem: false,
    },
    {
      path: '/account/recover',
      name: 'Recover Account',
      component: AccountRecover,
      route: Route,
      navItem: false,
    },
    {
      path: '/account/register',
      name: 'Register',
      component: Register,
      route: Route,
      navItem: false,
    },
    {
      path: '/account/register2fa',
      name: 'Register 2FA',
      component: Register2fa,
      route: Route,
      navItem: false,
    },
  ],
};

const errorRoutes = {
  path: '/error',
  name: 'Error',
  navItem: false,
  children: [
    {
      path: '/error/PageNotFound',
      name: 'Page Not Found',
      component: ErrorPageNotFound,
      route: Route,
      navItem: false,
    },
    {
      path: '/error/NotAuthorized',
      name: 'Not Authorized',
      component: NotAuthorizedPage,
      route: Route,
      navItem: false,
    },
    {
      path: '/error/UnderConstruction',
      name: 'Under Construction',
      component: UnderConstruction,
      route: Route,
      navItem: false,
    },
  ],
};

const PageNotFoundRoute = {
  path: '*',
  name: 'Page Not Found',
  component: ErrorPageNotFound,
  route: Route,
  navItem: false,
};

// flatten the list of all nested routes
const flattenRoutes = routes => {
  let flatRoutes = [];

  routes = routes || [];
  routes.forEach(item => {
    flatRoutes.push(item);

    if (typeof item.children !== 'undefined') {
      flatRoutes = [...flatRoutes, ...flattenRoutes(item.children)];
    }
  });
  return flatRoutes;
};

// All routes
const allRoutes = [
  rootRoute,
  authRoutes,
  homeDashbordRoutes,
  loanRoutes,
  securityRoutes,
  toolsRoutes,
  errorRoutes,
  userManagement,
  reportsRoutes,
  lenderRoutes,
  PropertiesRoutes,
  PageNotFoundRoute];

const authProtectedRoutes = [
  homeDashbordRoutes,
  loanRoutes,
  securityRoutes,
  lenderRoutes,
  PropertiesRoutes,
  reportsRoutes,
  userManagement,
  toolsRoutes,
]; 

const allFlattenRoutes = flattenRoutes(allRoutes);

export { allRoutes, authProtectedRoutes, allFlattenRoutes,};
